import React from 'react';
import moment from 'moment';
import gql from 'graphql-tag';
import { Subscription } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Icon,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Button,
  Chip,
  Select,
  MenuItem,
} from '@material-ui/core';
import Loading from '../utils/Loading';
import fragments from '../utils/graphql/fragments';
import BoothIdLink from '../reusable/BoothIdLink';
import OrderCard from '../reusable/OrderCard';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    backgroundColor: '#f3f4f6',
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '1.5rem',
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    padding: theme.spacing(2.5),
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
    },
    position: 'relative',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
    height: '100%',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statNumber: {
    fontSize: '2.8rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: 'auto',
  },
  numberRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    width: '100%',
  },
  statLabel: {
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    //marginLeft: theme.spacing(2),
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  applicantIcon: {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
  },
  messageIcon: {
    backgroundColor: 'rgba(25, 118, 210, 0.12)',
  },
  maintenanceIcon: {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
  },
  urgentIcon: {
    backgroundColor: 'rgba(229, 57, 53, 0.12)',
  },
  icon: {
    fontSize: '2.2rem',
    color: theme.palette.text.primary,
  },
  urgentCard: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  warningCard: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  infoCard: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  successCard: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  actionList: {
    maxHeight: 400,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: 'transparent',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0),
  },
  listItem: {
    position: 'relative',
    marginBottom: theme.spacing(1),
    borderRadius: '0 8px 8px 0',
    transition: 'background-color 0.2s ease, transform 0.2s ease',
    overflow: 'hidden',
    paddingLeft: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateX(4px)',
    },
  },
  itemBorder: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 4,
    height: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  urgentBorder: {
    backgroundColor: theme.palette.error.main,
  },
  warningBorder: {
    backgroundColor: theme.palette.warning.main,
  },
  successBorder: {
    backgroundColor: theme.palette.success.main,
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    margin: 0,
    fontSize: '1.1rem',
    '&:after': {
      display: 'none',
    },
  },
  badge: {
    marginLeft: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(4, 0),
    backgroundColor: theme.palette.divider,
  },
  statusChip: {
    margin: theme.spacing(0, 0.5),
  },
  booths: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
  },
  link: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 500,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
  },
  actionButton: {
    marginTop: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  chevronIcon: {
    fontSize: '2rem',
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s ease',
  },
  cardLink: {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
      '& $chevronIcon': {
        transform: 'translateX(4px)',
      },
    },
  },
  paper: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: theme.spacing(2),
  },
  orderItem: {
    padding: 0,
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  statusHeaderTitle: {
    fontWeight: 500,
  },
  statusCard: {
    border: '1px solid #e0e0e0',
    boxShadow: 'none',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.07)',
    },
  },
  statusCardContent: {
    padding: theme.spacing(2),
  },
  statusCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  statusCardValue: {
    fontWeight: 700,
    fontSize: '2rem',
    marginBottom: theme.spacing(0.5),
  },
  statusCardSubtitle: {
    display: 'block',
    fontSize: '0.75rem',
  },
  statusIconWrapper: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  statusIconDefault: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    color: theme.palette.text.secondary,
  },
  statusIconNew: {
    backgroundColor: 'rgba(255, 152, 0, 0.15)',
    color: '#EF6C00',
  },
  statusIconReady: {
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    color: '#1565C0',
  },
  statusIconOutbound: {
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    color: '#1565C0',
  },
  statusIconDelivered: {
    backgroundColor: 'rgba(76, 175, 80, 0.15)',
    color: '#2E7D32',
  },
  statusIconPacking: {
    backgroundColor: 'rgba(156, 39, 176, 0.15)',
    color: '#7B1FA2',
  },
  statusIconInbound: {
    backgroundColor: 'rgba(255, 152, 0, 0.15)',
    color: '#EF6C00',
  },
  statusIconReturned: {
    backgroundColor: 'rgba(96, 125, 139, 0.15)',
    color: '#455A64',
  },
  statusIconDone: {
    backgroundColor: 'rgba(120, 144, 156, 0.15)',
    color: '#546E7A',
  },
  statusIconCanceled: {
    backgroundColor: 'rgba(158, 158, 158, 0.15)',
    color: '#616161',
  },
  timelineContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2, 2, 2),
  },
  timelineItem: {
    position: 'relative',
    borderLeft: '4px solid',
    paddingLeft: theme.spacing(2),
    paddingRight: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderRadius: '0',
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateX(4px)',
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
  },
  urgentTimelineItem: {
    borderLeftColor: theme.palette.warning.main,
  },
  overdueTimelineItem: {
    borderLeftColor: theme.palette.error.main,
  },
  criticalTimelineItem: {
    borderLeftColor: theme.palette.error.dark,
    backgroundColor: 'rgba(244, 67, 54, 0.05)',
  },
  shippingTimelineItem: {
    borderLeftColor: theme.palette.primary.main,
  },
  returningTimelineItem: {
    borderLeftColor: theme.palette.success.main,
  },
  timelineContent: {
    width: '100%',
  },
  timelineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
  },
  timelineCustomer: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: 0,
  },
  timelineSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: 0,
  },
  timelineTag: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '1px 8px',
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    height: '18px',
  },
  urgentTag: {
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
    color: '#ED6C02',
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  overdueTag: {
    backgroundColor: 'rgba(229, 57, 53, 0.08)',
    color: '#D32F2F',
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  criticalTag: {
    backgroundColor: 'rgba(211, 47, 47, 0.15)',
    color: '#C62828',
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  shippingTag: {
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    color: '#1976d2',
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  returningTag: {
    backgroundColor: 'rgba(76, 175, 80, 0.08)',
    color: '#2E7D32',
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  timelineDates: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  timelineDateItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85rem',
  },
  timelineDate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
    fontSize: '0.8rem',
  },
  timelineDateIcon: {
    fontSize: '0.8rem',
    marginRight: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
  },
  sectionControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  filterSelect: {
    marginLeft: theme.spacing(1),
    minWidth: 120,
    fontSize: '0.875rem',
  },
  inventoryCard: {
    border: '1px solid #e0e0e0',
    boxShadow: 'none',
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.07)',
    },
  },
  inventoryCardContent: {
    padding: theme.spacing(2),
  },
  inventoryCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  inventoryValue: {
    fontWeight: 700,
    fontSize: '1.75rem',
    marginBottom: theme.spacing(0.5),
  },
  inventoryIconWrapper: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  scheduleIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1rem',
    color: 'inherit',
  },
  maintenanceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: '1px solid #edf2f7',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  maintenanceTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  inventoryGrid: {
    marginBottom: theme.spacing(2),
  },
  calendarButton: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    padding: '4px 10px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
  },
  eventDate: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  shippingIcon: {
    color: theme.palette.primary.main,
    fontSize: '0.9rem',
    marginRight: '4px',
  },
}));

const prepareDataInternal = orders => {
  if (!orders || !orders.length) return null;

  const today = moment().startOf('day');
  const startOfWeek = moment().startOf('week');
  const endOfWeek = moment().endOf('week');

  const hasBeenDroppedOff = order => {
    const tracking = order.outbound_shipment_activity;
    if (!tracking) return false;
    return tracking.shipped === true;
  };

  const getEffectiveShippingStatus = order => {
    if (order.status === 'Delivered') return 'Delivered';
    if (order.status === 'Outbound') {
      return hasBeenDroppedOff(order) ? 'Dropped Off' : 'Ready For Drop-Off';
    }
    return order.status;
  };

  const statusCounts = {};
  orders.forEach(order => {
    statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
  });

  const toShipSoon = orders
    .filter(order => {
      const needsPrep = ['New', 'Ready'].includes(order.status);
      const isOutboundNotDroppedOff = order.status === 'Outbound' && !hasBeenDroppedOff(order);
      if (!needsPrep && !isOutboundNotDroppedOff) return false;
      if (!order.ship_date) return false;
      const shipDate = moment(order.ship_date);
      if (!shipDate.isValid()) return false;
      return shipDate.isSameOrAfter(startOfWeek, 'day') && shipDate.isSameOrBefore(endOfWeek, 'day');
    })
    .sort((a, b) => moment(a.ship_date).diff(moment(b.ship_date)));

  const allShipThisWeek = orders
    .filter(order => {
      if (!order.ship_date) return false;
      const shipDate = moment(order.ship_date);
      if (!shipDate.isValid()) return false;
      return shipDate.isSameOrAfter(startOfWeek, 'day') && shipDate.isSameOrBefore(endOfWeek, 'day');
    })
    .sort((a, b) => moment(a.ship_date).diff(moment(b.ship_date)));

  allShipThisWeek.forEach(order => {
    order.effectiveStatus = getEffectiveShippingStatus(order);
  });

  console.log('All orders shipping this week:', allShipThisWeek.length);
  console.log('Orders to prepare for shipping this week (New/Ready/NotDroppedOff):', toShipSoon.length);
  console.log('Week range:', startOfWeek.format('YYYY-MM-DD'), 'to', endOfWeek.format('YYYY-MM-DD'));
  allShipThisWeek.forEach(order => {
    console.log(
      `Order ${order.id} - Status: ${order.status} - Effective: ${order.effectiveStatus} - Ship date: ${moment(
        order.ship_date
      ).format('YYYY-MM-DD')} - Event: ${order.event_name}`
    );
  });

  const returning = orders.filter(order => order.status === 'Inbound');
  const needsPacking = orders.filter(order => order.status === 'Packing');
  const urgentOrders = [];

  const shippingToday = orders.filter(order => {
    const needsPrep = ['New', 'Ready'].includes(order.status);
    const isOutboundNotDroppedOff = order.status === 'Outbound' && !hasBeenDroppedOff(order);
    if (!needsPrep && !isOutboundNotDroppedOff) return false;
    if (!order.ship_date) return false;
    const shipDate = moment(order.ship_date);
    return shipDate.isValid() && shipDate.isSame(today, 'day');
  });
  console.log(`Found ${shippingToday.length} orders shipping today`);
  urgentOrders.push(...shippingToday);

  const pastShipDateNotDroppedOff = orders.filter(order => {
    const needsPrep = ['New', 'Ready'].includes(order.status);
    const isOutboundNotDroppedOff = order.status === 'Outbound' && !hasBeenDroppedOff(order);
    if (!needsPrep && !isOutboundNotDroppedOff) return false;
    if (!order.ship_date) return false;
    const shipDate = moment(order.ship_date);
    return shipDate.isValid() && shipDate.isBefore(today, 'day');
  });
  console.log(`Found ${pastShipDateNotDroppedOff.length} orders past ship date not dropped off`);
  urgentOrders.push(...pastShipDateNotDroppedOff);

  const eventsToday = orders.filter(order => {
    const needsAction =
      ['New', 'Ready'].includes(order.status) || (order.status === 'Outbound' && !hasBeenDroppedOff(order));
    if (!needsAction || !order.event_date) return false;
    const eventDate = moment(order.event_date);
    return eventDate.isValid() && eventDate.isSame(today, 'day');
  });
  console.log(`Found ${eventsToday.length} events starting today that haven't been shipped`);
  urgentOrders.push(...eventsToday);

  const lateReturns = orders.filter(order => {
    if (!['Delivered', 'Packing'].includes(order.status) || !order.return_date) return false;
    const returnDate = moment(order.return_date);
    return returnDate.isValid() && returnDate.isBefore(today, 'day');
  });
  console.log(`Found ${lateReturns.length} late returns`);
  urgentOrders.push(...lateReturns);

  const needCheckin = orders.filter(order => order.status === 'Returned');
  console.log(`Found ${needCheckin.length} booths that need to be checked in`);
  urgentOrders.push(...needCheckin);

  const urgentOrdersUnique = Array.from(new Set(urgentOrders.map(order => order.id))).map(id =>
    urgentOrders.find(order => order.id === id)
  );

  console.log(`Total unique urgent orders: ${urgentOrdersUnique.length}`);

  return {
    statusCounts,
    toShipSoon,
    allShipThisWeek,
    returning,
    needsPacking,
    urgentOrders: urgentOrdersUnique,
    hasBeenDroppedOff,
    getEffectiveShippingStatus,
  };
};

function DashboardContent({
  apolloData,
  highestOrderIdSeen,
  setHighestOrderIdSeen,
  newOrdersCount,
  setNewOrdersCount,
  isBlinking,
  lastSeenTimestamp,
  markOrdersAsSeen,
  viewOrder,
  viewOrdersByStatus,
  history,
  classes,
}) {
  React.useEffect(() => {
    const orders = apolloData?.orders;
    if (orders && orders.length > 0) {
      const currentBatchMaxId = Math.max(...orders.map(order => order.id));
      const localStorageHighestId = parseInt(localStorage.getItem('highestOrderIdSeen') || '0', 10);

      if (currentBatchMaxId > highestOrderIdSeen) {
        setHighestOrderIdSeen(currentBatchMaxId);
        const newOrderItems = orders.filter(order => order.id > localStorageHighestId);
        setNewOrdersCount(newOrderItems.length);
      }
    }
  }, [apolloData, highestOrderIdSeen, setHighestOrderIdSeen, setNewOrdersCount]);

  const dashboardData = React.useMemo(() => {
    if (apolloData && apolloData.orders) {
      return prepareDataInternal(apolloData.orders);
    }
    return null;
  }, [apolloData]);

  if (!dashboardData) {
    return <Typography>No active orders found.</Typography>;
  }

  return (
    <>
      {/* Key Stats Section */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {/* New Orders Tile - Always visible */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.card}
            style={{
              backgroundColor: isBlinking ? 'rgba(76, 175, 80, 0.15)' : 'white',
              transition: 'background-color 0.3s ease-in-out',
              borderLeft: isBlinking ? '4px solid #4caf50' : '4px solid transparent',
              cursor: 'pointer',
            }}
            onClick={() => history.push('/orders?is_new=true')}
          >
            <CardContent className={classes.cardContent}>
              <div className={classes.titleRow}>
                <Box className={`${classes.iconContainer}`} style={{ backgroundColor: 'rgba(76, 175, 80, 0.12)' }}>
                  <Icon className={classes.icon}>fiber_new</Icon>
                </Box>
                <Typography className={classes.statLabel}>New Orders</Typography>
              </div>
              <div className={classes.numberRow}>
                <Typography className={classes.statNumber}>{newOrdersCount}</Typography>
                <Icon className={classes.chevronIcon}>chevron_right</Icon>
              </div>
              {lastSeenTimestamp && (
                <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginTop: '8px' }}>
                  Last viewed: {lastSeenTimestamp}
                </Typography>
              )}
              <Button
                variant='contained'
                color='primary'
                fullWidth
                onClick={e => {
                  e.stopPropagation();
                  markOrdersAsSeen();
                }}
                disabled={newOrdersCount === 0}
                style={{
                  marginTop: '16px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  padding: '8px 16px',
                  opacity: newOrdersCount === 0 ? 0.7 : 1,
                }}
                startIcon={<Icon>visibility</Icon>}
              >
                Mark as Seen
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.card}
            onClick={() => history.push('/outbound-schedule')}
            style={{ cursor: 'pointer' }}
          >
            <CardContent className={classes.cardContent}>
              <div className={classes.titleRow}>
                <Box className={`${classes.iconContainer} ${classes.messageIcon}`}>
                  <Icon className={classes.icon}>local_shipping</Icon>
                </Box>
                <Typography className={classes.statLabel}>To Ship This Week</Typography>
              </div>
              <div className={classes.numberRow}>
                <Typography className={classes.statNumber}>{dashboardData.allShipThisWeek.length}</Typography>
                <Icon className={classes.chevronIcon}>chevron_right</Icon>
              </div>

              {/* Shipping status summary */}
              {dashboardData.allShipThisWeek.length > 0 && (
                <Box mt={1}>
                  <Grid container spacing={1}>
                    {(() => {
                      // Count orders by effective shipping status
                      const needsPrep = dashboardData.allShipThisWeek.filter(o => o.status === 'New').length;

                      // Ready includes both Ready status and Outbound status without tracking movement
                      const readyToShip = dashboardData.allShipThisWeek.filter(
                        o => o.status === 'Ready' || (o.status === 'Outbound' && !dashboardData.hasBeenDroppedOff(o))
                      ).length;

                      // Only count as shipped if it's delivered or has actual shipping movement
                      const alreadyShipped = dashboardData.allShipThisWeek.filter(
                        o => o.status === 'Delivered' || (o.status === 'Outbound' && dashboardData.hasBeenDroppedOff(o))
                      ).length;

                      return (
                        <>
                          {needsPrep > 0 && (
                            <Grid item>
                              <Typography
                                variant='body2'
                                style={{
                                  color: '#f57c00',
                                  fontWeight: 500,
                                  fontSize: '0.85rem',
                                  backgroundColor: 'rgba(245, 124, 0, 0.1)',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                }}
                              >
                                {needsPrep} Need Prep
                              </Typography>
                            </Grid>
                          )}

                          {readyToShip > 0 && (
                            <Grid item>
                              <Typography
                                variant='body2'
                                style={{
                                  color: '#1976d2',
                                  fontWeight: 500,
                                  fontSize: '0.85rem',
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                }}
                              >
                                {readyToShip} Ready For Drop-Off
                              </Typography>
                            </Grid>
                          )}

                          {alreadyShipped > 0 && (
                            <Grid item>
                              <Typography
                                variant='body2'
                                style={{
                                  color: '#4caf50',
                                  fontWeight: 500,
                                  fontSize: '0.85rem',
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                }}
                              >
                                {alreadyShipped} In Transit
                              </Typography>
                            </Grid>
                          )}
                        </>
                      );
                    })()}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.card} onClick={() => viewOrdersByStatus('Inbound')} style={{ cursor: 'pointer' }}>
            <CardContent className={classes.cardContent}>
              <div className={classes.titleRow}>
                <Box className={`${classes.iconContainer} ${classes.scheduleIcon}`}>
                  <Icon className={classes.icon}>assignment_return</Icon>
                </Box>
                <Typography className={classes.statLabel}>Returning to Warehouse</Typography>
              </div>
              <div className={classes.numberRow}>
                <Typography className={classes.statNumber}>{dashboardData.returning.length}</Typography>
                <Icon className={classes.chevronIcon}>chevron_right</Icon>
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={classes.card}
            style={{
              backgroundColor: dashboardData.urgentOrders.some(
                order =>
                  ['New', 'Ready'].includes(order.status) &&
                  moment(order.event_date).isSame(moment().startOf('day'), 'day')
              )
                ? 'rgba(229, 57, 53, 0.12)'
                : undefined,
              cursor: 'pointer',
            }}
            onClick={() => history.push('/orders?urgent=true')}
          >
            <CardContent className={classes.cardContent}>
              <div className={classes.titleRow}>
                <Box className={`${classes.iconContainer} ${classes.urgentIcon}`}>
                  <Icon className={classes.icon}>priority_high</Icon>
                </Box>
                <Typography className={classes.statLabel}>Need Immediate Attention</Typography>
              </div>
              <div className={classes.numberRow}>
                <Typography className={classes.statNumber}>{dashboardData.urgentOrders.length}</Typography>
                <Icon className={classes.chevronIcon}>chevron_right</Icon>
              </div>
              {dashboardData.urgentOrders.some(
                order =>
                  ['New', 'Ready'].includes(order.status) &&
                  moment(order.event_date).isSame(moment().startOf('day'), 'day')
              ) && (
                <Typography
                  variant='body2'
                  style={{
                    color: '#e53935',
                    fontWeight: 'bold',
                    marginTop: '8px',
                  }}
                >
                  CRITICAL: Event scheduled today!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      {/* Urgent Actions Section */}
      {dashboardData.urgentOrders.length > 0 && (
        <Paper className={classes.paper} elevation={0}>
          <div className={classes.sectionHeader}>
            <Typography variant='h6' className={classes.sectionTitle}>
              Urgent Actions
            </Typography>
            <div className={classes.sectionControls}>
              <Chip label={`${dashboardData.urgentOrders.length} items`} size='small' color='secondary' />
              <Button
                size='small'
                color='primary'
                startIcon={<Icon>filter_list</Icon>}
                onClick={() => history.push('/orders?urgent=true')}
              >
                View All
              </Button>
            </div>
          </div>
          <Box className={classes.timelineContainer}>
            {dashboardData.urgentOrders.map(order => {
              // Determine status details and urgency level
              const isHighUrgency =
                order.status === 'New' && moment(order.event_date).isSame(moment().startOf('day'), 'day');

              const isOverdue =
                (order.status === 'Packing' && moment(order.return_date).isBefore(moment(), 'day')) ||
                ((order.status === 'New' || order.status === 'Ready') &&
                  moment(order.ship_date).isBefore(moment(), 'day'));

              // Calculate how many days late if overdue
              let daysLate = 0;
              if (order.status === 'Packing' && moment(order.return_date).isValid()) {
                daysLate = moment().diff(moment(order.return_date), 'days');
              } else if (moment(order.ship_date).isValid()) {
                daysLate = moment().diff(moment(order.ship_date), 'days');
              }

              // Determine tag text based on status
              let urgencyTag = '';
              if (isHighUrgency) {
                urgencyTag = 'EVENT TODAY';
              } else if (isOverdue) {
                urgencyTag = `${daysLate} DAYS LATE`;
              } else if (order.status === 'Returned') {
                urgencyTag = 'NEEDS CHECK-IN';
              } else if (order.status === 'Packing') {
                urgencyTag = 'AWAITING RETURN';
              } else {
                urgencyTag = 'SHIP ASAP';
              }

              return (
                <Box
                  key={order.id}
                  className={`${classes.timelineItem} ${
                    isHighUrgency
                      ? classes.criticalTimelineItem
                      : isOverdue
                      ? classes.overdueTimelineItem
                      : classes.urgentTimelineItem
                  }`}
                  onClick={() => viewOrder(order.id)}
                >
                  <div className={classes.timelineContent}>
                    <div className={classes.timelineHeader}>
                      <Typography variant='subtitle1' className={classes.timelineCustomer}>
                        {order.customer_name || 'Customer'} ({order.status})
                      </Typography>
                      <div className={classes.timelineDate}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon fontSize='small' className={classes.shippingIcon}>
                            local_shipping
                          </Icon>
                          <Typography variant='body2' component='span' style={{ fontSize: '0.8rem' }}>
                            Shipping: {moment(order.ship_date).format('MMM D')}
                          </Typography>
                        </div>
                        <div
                          className={`${classes.timelineTag} ${
                            isHighUrgency ? classes.criticalTag : isOverdue ? classes.overdueTag : classes.urgentTag
                          }`}
                        >
                          {urgencyTag}
                        </div>
                      </div>
                    </div>
                    <Typography variant='body2' className={classes.timelineSubtitle}>
                      {order.event_name} • {order.shipping_city}, {order.shipping_state} •{' '}
                      <BoothIdLink boothId={order.booth_id} />
                    </Typography>
                    <Box className={classes.timelineDates}>
                      {order.ship_date && (
                        <Box className={classes.timelineDateItem}>
                          <Icon fontSize='small' className={classes.timelineDateIcon}>
                            local_shipping
                          </Icon>
                          <Typography variant='body2' component='span'>
                            Ship: {moment(order.ship_date).format('MMM D')}
                          </Typography>
                        </Box>
                      )}
                      {order.event_date && (
                        <Box className={classes.timelineDateItem}>
                          <Icon fontSize='small' className={classes.timelineDateIcon}>
                            event
                          </Icon>
                          <Typography variant='body2' component='span'>
                            Event: {moment(order.event_date).format('MMM D')}
                          </Typography>
                        </Box>
                      )}
                      {order.return_date && (
                        <Box className={classes.timelineDateItem}>
                          <Icon fontSize='small' className={classes.timelineDateIcon}>
                            assignment_return
                          </Icon>
                          <Typography variant='body2' component='span'>
                            Return: {moment(order.return_date).format('MMM D')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </div>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
      <Grid container spacing={2}>
        {/* Shipping This Week Section */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper} elevation={0}>
            <div className={classes.sectionHeader}>
              <Typography variant='h6' className={classes.sectionTitle}>
                Shipping This Week
              </Typography>
              <div className={classes.sectionControls}>
                <Button
                  className={classes.calendarButton}
                  startIcon={<Icon fontSize='small'>calendar_today</Icon>}
                  onClick={() => history.push('/outbound-schedule')}
                >
                  View Calendar
                </Button>
              </div>
            </div>
            {dashboardData.allShipThisWeek.length > 0 ? (
              <Box className={classes.timelineContainer}>
                {dashboardData.allShipThisWeek.map(order => {
                  // Determine whether item has been shipped
                  const isShipped =
                    order.status === 'Delivered' ||
                    (order.status === 'Outbound' &&
                      order.outbound_shipment_activity &&
                      order.outbound_shipment_activity.shipped === true);

                  // Determine shipping tag text
                  let shippingTag = '';
                  if (isShipped) {
                    shippingTag = 'IN TRANSIT';
                  } else if (order.status === 'Ready') {
                    shippingTag = 'READY FOR OUTBOUND';
                  } else if (order.status === 'Outbound') {
                    shippingTag = 'NEEDS DROP-OFF';
                  } else {
                    shippingTag = 'NEEDS PREP';
                  }

                  // Calculate days until shipping
                  const daysToShip = moment(order.ship_date).diff(moment(), 'days');
                  const shipDateText =
                    daysToShip === 0 ? 'Today' : daysToShip === 1 ? 'Tomorrow' : `in ${daysToShip} days`;

                  return (
                    <Box
                      key={order.id}
                      className={`${classes.timelineItem} ${classes.shippingTimelineItem}`}
                      onClick={() => viewOrder(order.id)}
                    >
                      <div className={classes.timelineContent}>
                        <div className={classes.timelineHeader}>
                          <Box>
                            <BoothIdLink boothId={order.booth_id} />
                            <Typography
                              variant='subtitle1'
                              className={classes.timelineCustomer}
                              style={{ marginTop: '4px' }}
                            >
                              {order.event_name || 'Customer'}
                            </Typography>
                          </Box>
                          <div className={classes.timelineDate}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Icon fontSize='small' className={classes.shippingIcon}>
                                local_shipping
                              </Icon>
                              <Typography variant='body2' component='span' style={{ fontSize: '0.8rem' }}>
                                Shipping: {moment(order.ship_date).format('MMM D')}
                              </Typography>
                            </div>
                            <div className={`${classes.timelineTag} ${classes.shippingTag}`}>{shippingTag}</div>
                          </div>
                        </div>
                        <Typography variant='body2' className={classes.timelineSubtitle}>
                          {order.shipping_city}, {order.shipping_state} • #{order.id}
                        </Typography>
                      </div>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box p={3} textAlign='center'>
                <Typography variant='body1' color='textSecondary'>
                  No orders shipping this week
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Inventory Status Section (replacing Booths Returning) */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper} elevation={0}>
            <div className={classes.sectionHeader}>
              <Typography variant='h6' className={classes.sectionTitle}>
                Order Counts
              </Typography>
              <div className={classes.sectionControls}>
                <Button size='small' color='primary' onClick={() => history.push('/inventory')}>
                  Generate Report
                </Button>
              </div>
            </div>
            <Box p={3}>
              <Grid container spacing={2} className={classes.inventoryGrid}>
                {/* Inventory Status Cards */}
                {Object.entries(dashboardData.statusCounts)
                  .slice(0, 4)
                  .map(([status, count]) => {
                    // Configure card based on status
                    let iconName = 'inventory_2';
                    let iconColor = '';
                    let subtitle = '';

                    switch (status) {
                      case 'New':
                        iconName = 'fiber_new';
                        iconColor = 'rgba(255, 152, 0, 0.15)';
                        subtitle = 'need preparation';
                        break;
                      case 'Outbound':
                        iconName = 'send';
                        iconColor = 'rgba(33, 150, 243, 0.15)';
                        subtitle = 'en route to events';
                        break;
                      case 'Ready':
                        iconName = 'local_shipping';
                        iconColor = 'rgba(76, 175, 80, 0.15)';
                        subtitle = 'to be shipped';
                        break;
                      case 'Delivered':
                        iconName = 'check_circle';
                        iconColor = 'rgba(76, 175, 80, 0.15)';
                        subtitle = 'at customer location';
                        break;
                      case 'Returned':
                        iconName = 'assignment_turned_in';
                        iconColor = 'rgba(255, 152, 0, 0.15)';
                        subtitle = 'need check-in';
                        break;
                      case 'Packing':
                        iconName = 'inventory_2';
                        iconColor = 'rgba(156, 39, 176, 0.15)';
                        subtitle = 'being packed by customer';
                        break;
                      case 'Inbound':
                        iconName = 'assignment_return';
                        iconColor = 'rgba(255, 152, 0, 0.15)';
                        subtitle = 'returning to warehouse';
                        break;
                      case 'Done':
                        iconName = 'task_alt';
                        iconColor = 'rgba(120, 144, 156, 0.15)';
                        subtitle = 'completed orders';
                        break;
                      case 'Canceled':
                        iconName = 'cancel';
                        iconColor = 'rgba(158, 158, 158, 0.15)';
                        subtitle = 'canceled orders';
                        break;
                      default:
                        iconColor = 'rgba(0, 0, 0, 0.08)';
                        subtitle = 'in this status';
                    }

                    return (
                      <Grid item xs={6} key={status}>
                        <Card className={classes.inventoryCard} onClick={() => viewOrdersByStatus(status)}>
                          <Box className={classes.inventoryCardContent}>
                            <Box className={classes.inventoryCardHeader}>
                              <Typography variant='body2' color='textSecondary'>
                                {status}
                              </Typography>
                              <Box className={classes.inventoryIconWrapper} style={{ backgroundColor: iconColor }}>
                                <Icon fontSize='small'>{iconName}</Icon>
                              </Box>
                            </Box>
                            <Typography variant='h4' className={classes.inventoryValue}>
                              {count}
                            </Typography>
                            <Typography variant='caption' color='textSecondary'>
                              {subtitle}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>

              {/* Maintenance Schedule */}
              <Box mt={3}>
                <Typography variant='subtitle2' className={classes.maintenanceTitle}>
                  MAINTENANCE SCHEDULE
                </Typography>
                <div className={classes.maintenanceItem}>
                  <Box display='flex' alignItems='center'>
                    <Icon className={classes.scheduleIcon} style={{ color: '#f57c00' }}>
                      schedule
                    </Icon>
                    <Typography variant='body2'>Booth #12 Routine Cleaning</Typography>
                  </Box>
                  <Typography variant='body2' color='textSecondary'>
                    Today
                  </Typography>
                </div>
                <div className={classes.maintenanceItem}>
                  <Box display='flex' alignItems='center'>
                    <Icon className={classes.scheduleIcon} style={{ color: '#1976d2' }}>
                      schedule
                    </Icon>
                    <Typography variant='body2'>Camera Firmware Updates</Typography>
                  </Box>
                  <Typography variant='body2' color='textSecondary'>
                    Tomorrow
                  </Typography>
                </div>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      ; ; ;
    </>
  );
}

function Dashboard(props) {
  const classes = useStyles();
  const [highestOrderIdSeen, setHighestOrderIdSeen] = React.useState(0);
  const [newOrdersCount, setNewOrdersCount] = React.useState(0);
  const [isBlinking, setIsBlinking] = React.useState(false);
  const [lastSeenTimestamp, setLastSeenTimestamp] = React.useState('');

  React.useEffect(() => {
    const storedHighestId = localStorage.getItem('highestOrderIdSeen');
    const storedTimestamp = localStorage.getItem('lastSeenTimestamp');
    if (storedHighestId) {
      setHighestOrderIdSeen(parseInt(storedHighestId, 10));
    }
    if (storedTimestamp) {
      setLastSeenTimestamp(storedTimestamp);
    }
  }, []);

  React.useEffect(() => {
    let blinkInterval;
    if (newOrdersCount > 0) {
      blinkInterval = setInterval(() => {
        setIsBlinking(prev => !prev);
      }, 800);
    } else {
      setIsBlinking(false);
    }
    return () => {
      if (blinkInterval) {
        clearInterval(blinkInterval);
      }
    };
  }, [newOrdersCount]);

  const markOrdersAsSeen = () => {
    if (newOrdersCount > 0) {
      const now = new Date();
      const timestamp = now.toLocaleString();
      localStorage.setItem('highestOrderIdSeen', highestOrderIdSeen.toString());
      localStorage.setItem('lastSeenTimestamp', timestamp);
      setLastSeenTimestamp(timestamp);
      setNewOrdersCount(0);
      console.log(`Marked orders as seen. Highest ID set in localStorage: ${highestOrderIdSeen}`);
    }
  };

  const viewOrder = orderId => {
    props.history.push(`/orders/${orderId}`);
  };

  const viewOrdersByStatus = status => {
    props.history.push(`/orders?status=${status}`);
  };

  return (
    <div
      className={classes.root}
      style={{
        backgroundColor: isBlinking ? 'rgba(76, 175, 80, 0.15)' : '#f3f4f6',
        transition: 'background-color 0.3s ease-in-out',
      }}
    >
      <Container maxWidth='lg'>
        <Typography variant='h4' className={classes.title}>
          Operations Dashboard
        </Typography>
        <Subscription subscription={GET_DASHBOARD_ORDERS}>
          {({ loading, error, data: apolloData }) => {
            if (loading) return <Loading fixed />;
            if (error) return <Typography color='error'>Error loading dashboard data: {error.message}</Typography>;

            return (
              <DashboardContent
                apolloData={apolloData}
                highestOrderIdSeen={highestOrderIdSeen}
                setHighestOrderIdSeen={setHighestOrderIdSeen}
                newOrdersCount={newOrdersCount}
                setNewOrdersCount={setNewOrdersCount}
                isBlinking={isBlinking}
                lastSeenTimestamp={lastSeenTimestamp}
                markOrdersAsSeen={markOrdersAsSeen}
                viewOrder={viewOrder}
                viewOrdersByStatus={viewOrdersByStatus}
                history={props.history}
                classes={classes}
              />
            );
          }}
        </Subscription>
      </Container>
    </div>
  );
}

const GET_DASHBOARD_ORDERS = gql`
  subscription getDashboardOrders {
    orders(where: { active: { _eq: true }, status: { _nin: ["Done", "Canceled"] } }) {
      ...Order
    }
  }
  ${fragments.order}
  ${fragments.shipmentActivity}
`;

export default Dashboard;